import type { SrsRating, SrsState, WordStatus } from '../types/index';

/**
 * 间隔重复调度（SM-2 变体，纯函数）。
 *
 * 为什么自己写而不是照抄 SuperMemo 2：
 * - SM-2 的原始公式只认「质量分 0–5」并直接把 EF 乘进间隔，遇到"完全忘记"时会
 *   把间隔压到 1 天，对**背单词**（而不是背长答案）太钝：一个刚学过的词，
 *   当天想再看一眼是合理需求。
 * - 因此这里保留 SM-2 的骨架（ease 因子 + 间隔累乘 + 遗忘降级），但把两处改成
 *   更适合单词卡的行为：
 *   1. **忘记（0）回到 10 分钟后**，而不是第二天 —— 当次会话内再见到一次，
 *      记忆保持率明显更高（也是 Anki 的 learning steps 做法）；
 *   2. **间隔上限 365 天**，避免 ease 连乘把间隔推到十年后（那等于"再也不复习"）。
 *
 * 这个文件被后端（写库）与前端（展示"下次 6 天后"）同时使用，所以：
 * **必须零依赖、零 I/O、不读时钟** —— `now` 一律由调用方传入。
 */

/** ease 的下限（SM-2 惯例）。低于它会让间隔几乎不再增长，永远刷不完。 */
export const SRS_MIN_EASE = 1.3;
/** ease 的上限。留一点空间给"秒答"奖励，但不能让间隔爆炸。 */
export const SRS_MAX_EASE = 3.2;
/** 间隔上限（天）。 */
export const SRS_MAX_INTERVAL_DAYS = 365;
/** 忘记后重新出现的等待时间（毫秒）。 */
export const SRS_RELEARN_DELAY_MS = 10 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** 调度算法的输入状态（与 `user_word_records` 的 SRS 列一一对应）。 */
export interface SrsInput {
  srsState: SrsState;
  ease: number;
  intervalDays: number;
  reps: number;
  lapses: number;
  /** 当前熟悉度 0–100（用于 status 推导，不参与间隔计算） */
  familiarity: number;
}

/** 调度结果：直接写回数据库的字段 + 一句人话解释。 */
export interface SrsSchedule {
  srsState: SrsState;
  ease: number;
  intervalDays: number;
  reps: number;
  lapses: number;
  dueAt: number;
  familiarity: number;
  /** 由熟悉度推导的生词本状态 */
  status: WordStatus;
  /** 如「记得 → 下次 6 天后（第 3 次复习）」 */
  explanation: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 熟悉度增量：评分越高涨得越多，忘记则倒退。 */
function familiarityDelta(rating: SrsRating): number {
  switch (rating) {
    case 0:
      return -25;
    case 1:
      return 5;
    case 2:
      return 12;
    case 3:
      return 20;
    case 4:
      return 28;
  }
}

/**
 * 熟悉度 → 生词本状态。
 *
 * 阈值刻意与"复习时用户会怎么自评"对齐：只有连续秒答且熟悉度很高的词才算
 * `mastered`（= 从复习队列里毕业），否则用户会看到一堆"已掌握"却依然想复习的词。
 */
export function statusForFamiliarity(familiarity: number, rating?: SrsRating): WordStatus {
  if (rating === 0) return 'unknown';
  if (familiarity >= 90) return 'mastered';
  if (familiarity >= 65) return 'known';
  if (familiarity >= 30) return 'fuzzy';
  if (rating === undefined || rating >= 2) return familiarity > 0 ? 'fuzzy' : 'new';
  return 'unknown';
}

function formatInterval(intervalDays: number, dueAt: number, now: number): string {
  const minutes = Math.max(1, Math.round((dueAt - now) / 60000));
  if (intervalDays <= 0) return `${minutes} 分钟后`;
  if (intervalDays === 1) return '明天';
  if (intervalDays < 30) return `${intervalDays} 天后`;
  const months = Math.round(intervalDays / 30);
  return months >= 12 ? `约 ${Math.round(intervalDays / 365)} 年后` : `约 ${months} 个月后`;
}

export const SRS_RATING_HINTS: Record<SrsRating, string> = {
  0: '完全忘记：今天再看一次',
  1: '很困难：间隔小幅增加',
  2: '有点模糊：间隔中等增加',
  3: '记得：按熟练度正常拉长',
  4: '秒答：ease 上调，间隔拉长最多',
};

/**
 * 计算下一次复习时间。
 *
 * @param prev   当前状态（新卡请传 `NEW_SRS_INPUT`）
 * @param rating 本次自评 0–4
 * @param now    当前时间（epoch ms），由调用方注入以便测试
 */
export function scheduleReview(prev: SrsInput, rating: SrsRating, now: number): SrsSchedule {
  let { ease, reps, lapses } = prev;
  const prevInterval = Math.max(0, prev.intervalDays);
  let intervalDays: number;
  let srsState: SrsState;

  if (rating === 0) {
    // 忘记：降级 + 回到学习队列（10 分钟后）
    lapses += 1;
    reps = 0;
    ease = clamp(ease - 0.2, SRS_MIN_EASE, SRS_MAX_EASE);
    intervalDays = 0;
    srsState = 'lapsed';
  } else if (rating === 1) {
    ease = clamp(ease - 0.15, SRS_MIN_EASE, SRS_MAX_EASE);
    reps += 1;
    intervalDays = prevInterval <= 0 ? 1 : Math.max(1, Math.round(prevInterval * 1.2));
    srsState = intervalDays < 7 ? 'learning' : 'review';
  } else if (rating === 2) {
    ease = clamp(ease - 0.05, SRS_MIN_EASE, SRS_MAX_EASE);
    reps += 1;
    intervalDays = prevInterval <= 0 ? 1 : Math.max(1, Math.round(prevInterval * 1.5));
    srsState = intervalDays < 7 ? 'learning' : 'review';
  } else if (rating === 3) {
    reps += 1;
    if (prevInterval <= 0) intervalDays = 1;
    else if (prevInterval <= 1) intervalDays = 3;
    else intervalDays = Math.round(prevInterval * ease);
    srsState = 'review';
  } else {
    // 秒答：小幅奖励 ease，间隔额外乘 1.3
    ease = clamp(ease + 0.1, SRS_MIN_EASE, SRS_MAX_EASE);
    reps += 1;
    if (prevInterval <= 0) intervalDays = 3;
    else intervalDays = Math.max(prevInterval + 1, Math.round(prevInterval * ease * 1.3));
    srsState = 'review';
  }

  intervalDays = clamp(intervalDays, 0, SRS_MAX_INTERVAL_DAYS);
  const dueAt = intervalDays === 0 ? now + SRS_RELEARN_DELAY_MS : now + intervalDays * DAY_MS;
  const familiarity = clamp(Math.round(prev.familiarity + familiarityDelta(rating)), 0, 100);

  const label = ['完全忘记', '很困难', '有点模糊', '记得', '秒答'][rating];
  const suffix = rating === 0 ? '（本会话内会再出现一次）' : `（第 ${reps} 次复习）`;

  return {
    srsState,
    ease: Number(ease.toFixed(2)),
    intervalDays,
    reps,
    lapses,
    dueAt,
    familiarity,
    status: statusForFamiliarity(familiarity, rating),
    explanation: `${label} → 下次 ${formatInterval(intervalDays, dueAt, now)}${suffix}`,
  };
}

/** 新卡的初始 SRS 参数（与 `user_word_records` 的默认值一致）。 */
export const NEW_SRS_INPUT: SrsInput = {
  srsState: 'new',
  ease: 2.5,
  intervalDays: 0,
  reps: 0,
  lapses: 0,
  familiarity: 0,
};

/** 强制重学：把卡片退回学习队列（"这个词我完全不记得了"）。 */
export function resetSchedule(now: number): SrsSchedule {
  return {
    srsState: 'lapsed',
    ease: 2.5,
    intervalDays: 0,
    reps: 0,
    lapses: 0,
    dueAt: now,
    familiarity: 0,
    status: 'unknown',
    explanation: '已重置为待复习（熟悉度归零）',
  };
}

/**
 * 到什么程度算"今天要复习"。
 *
 * 不是简单的 `dueAt <= now`：新学过的词 `dueAt` 可能是十分钟后，
 * 若用户在十分钟内又打开复习页，会看到一张"还没到期"的卡而困惑。
 * 因此把"已到期"放宽一个容差（默认 15 分钟），
 * 效果是"刚点完不认识，马上就想再看一遍"这种真实行为不会被算法挡住。
 */
export const DUE_TOLERANCE_MS = 15 * 60 * 1000;

export function isDue(dueAt: number | null, now: number, toleranceMs = DUE_TOLERANCE_MS): boolean {
  if (dueAt === null) return true; // 从未排期 = 新卡，随时可复习
  return dueAt <= now + toleranceMs;
}

/** 把间隔天数换算成"可读的一天"粒度，用于统计"本周新增到期"。 */
export function dueWithinDays(now: number, days: number): number {
  return now + days * DAY_MS;
}
